import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { confirmAlert } from 'react-confirm-alert';
import './NotificationsRegistryNew.scss';

import { createNotificationRegistryEntry, draftForNewNotification, getNotificationRegistryOptions } from '../../api/notifications';
import InfoTip from './elements/InfoTip';
import DraftButton from './elements/DraftButton';
import ButtonVerbField from './elements/ButtonVerbField';
import {
  AUDIENCE_LABELS,
  CENTER_BEHAVIOR_LABELS,
  CENTER_IMAGE_LABELS,
  CHANNEL_LABELS,
  SENT_FROM_CHOICES,
  SENT_FROM_UNDECIDED,
  DELIVERED_BY_KLAVIYO_COPY_NOTE,
  DELIVERED_BY_KLAVIYO_EMAIL_BODY,
  DELIVERED_BY_KLAVIYO_PLANNED_EVENT_HINT,
  KLAVIYO_FLOW_LINK_PLANNED_HINT,
  backendDeliveredByKlaviyo,
  usesKlaviyoFlow,
  sentFromChoiceOf,
  sentFromFieldsFor,
  SURFACE_LABELS,
  SURFACE_NOTES,
  SURFACE_FIELDS,
  applicableSurfaces,
  isKlaviyoOwnedCopySurface,
  KLAVIYO_EMAIL_COPY_NOTE,
  KLAVIYO_PUSH_COPY_NOTE,
  copyFieldLabel,
  fieldGuideFor,
  isParagraphField,
  hasCopyWords,
  interpolateWithExamples,
  DRAFTABLE_SURFACES,
  canDraftFromPlan,
  sendsFromBackend,
  modeChangeConfirm,
  TIPS,
  AUTO_LIVE_NOTE,
  AUTO_LIVE_REQUIRES_NO,
  humanizeBudgetCategory,
  parseInputsText,
  formatInputsText,
  shortCodeFrom,
  settingsGroupsFor,
  parseStatuses,
  casesOf,
  CASE_OPERATORS,
  CASE_OPERATOR_LABELS,
  CASE_FIELDS,
  caseFieldLabel,
  statusCases,
  statusLabelOf,
  isEntityThread
} from '../../helpers/notification_registry_helpers';
import SwitchBuilder, { switchProposalGaps } from './elements/SwitchBuilder';
import { Field, Chips } from './elements/RegistryField';
import SentFromSelect from './elements/SentFromSelect';

const NO_SWITCH = 'none';
const NEW_SWITCH = '__new__';

const emptyForm = {
  name: '',
  type: '',
  typeEdited: false,
  sentFrom: '',
  declaredPushDelivery: null,
  declaredEmailDelivery: null,
  declaredKlaviyoEventName: '',
  audiences: ['creator'],
  declaredBudgetCategory: 'operational',
  declaredUserNotificationSetting: '',
  noSwitchReason: '',
  newSwitchDefinition: null,
  declaredCenterBehavior: 'own_row',
  declaredCenterWindow: 'day',
  declaredCenterAnchor: '',
  declaredCenterImageOf: 'general',
  declaredRequiresAction: false,
  declaredCtaStyle: 'primary',
  declaredShowChevron: false,
  declaredThreadEntity: '',
  declaredThreadStatuses: '',
  productAreas: [],
  Owner_User_id: null,
  ownerEdited: false,
  declaredChannels: ['push', 'center'],
  purpose: '',
  triggerDescription: '',
  inputsText: '',
  tapDestination: '',
  emergencyOff: '',
  klaviyoFlowUrl: '',
  notificationCenterRationale: '',
  notes: '',
  shadowPeriodDays: 0,
  holdoutPercent: 0,
  autoLive: false,
  copy: {},
  // What the AI wrote, as it wrote it: a surface or an input still equal to this is the AI's to rewrite.
  aiCopy: {},
  aiInputs: {}
};

// Where a notification starts. Free text only where there is no list; everything else is chosen
// from a list the system already has. Copy is optional here and saves as drafts.
const NotificationsRegistryNew = ({ onCancel, onCreated, canChangeRollout = false, stickyTop = 0 }) => {
  const [options, setOptions] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [isCustomVerb, setIsCustomVerb] = useState(false);

  useEffect(() => {
    getNotificationRegistryOptions()
      .then(setOptions)
      .catch(() => window.ALERT?.error?.('Failed to load the form options.'));
  }, []);

  const set = (field, value) => setForm(current => ({ ...current, [field]: value }));
  const setCenterShape = patch =>
    setForm(current => {
      const next = { ...current, ...patch };
      const thread = isEntityThread({ centerBehavior: next.declaredCenterBehavior, centerWindow: next.declaredCenterWindow });
      const caseSurface = next.declaredCenterBehavior === 'aggregated' ? (thread ? 'center_row' : 'center_row_several') : null;
      return {
        ...next,
        copy: _.mapValues(next.copy, (content, surface) => {
          const withoutCases = surface === caseSurface ? content : _.omit(content, 'cases');
          return thread && surface === 'center_row' ? _.omit(withoutCases, 'text') : withoutCases;
        })
      };
    });
  const setName = name => setForm(current => ({ ...current, name, type: current.typeEdited ? current.type : shortCodeFrom(name) }));
  const toggle = (field, value) => set(field, form[field].includes(value) ? form[field].filter(v => v !== value) : [...form[field], value]);
  const toggleAudience = value => {
    const audiences = form.audiences.includes(value) ? form.audiences.filter(v => v !== value) : [...form.audiences, value];
    const switchesChange = !_.isEqual(
      settingsGroupsFor(options, audiences).map(group => group.id),
      settingsGroupsFor(options, form.audiences).map(group => group.id)
    );
    setForm(current => ({
      ...current,
      audiences,
      ...(switchesChange ? { declaredUserNotificationSetting: '', newSwitchDefinition: null } : {})
    }));
  };

  // The audience's usual owner is the default until someone picks one, "Nobody yet" included.
  useEffect(() => {
    if (!options || form.ownerEdited) return;
    set('Owner_User_id', options.ownerDefaultsByAudience?.[form.audiences[0]] || null);
  }, [options, form.audiences, form.ownerEdited]);

  const isCenter = form.declaredChannels.includes('center');
  const isThread = isCenter && isEntityThread({ centerBehavior: form.declaredCenterBehavior, centerWindow: form.declaredCenterWindow });
  const threadStatuses = isThread ? parseStatuses(form.declaredThreadStatuses) : [];
  const surfaces = useMemo(
    () =>
      applicableSurfaces({
        channels: form.declaredChannels,
        centerBehavior: isCenter ? form.declaredCenterBehavior : 'none',
        centerWindow: form.declaredCenterWindow,
        requiresAction: isCenter && form.declaredRequiresAction
      }),
    [form.declaredChannels, form.declaredCenterBehavior, form.declaredCenterWindow, form.declaredRequiresAction, isCenter]
  );
  const sentFromChoice = sentFromChoiceOf({ sentFrom: form.sentFrom, pushDelivery: form.declaredPushDelivery });
  const isDeliveredByKlaviyo = backendDeliveredByKlaviyo({ sentFrom: form.sentFrom, pushDelivery: form.declaredPushDelivery });
  const usesFlow = usesKlaviyoFlow({ sentFrom: form.sentFrom, pushDelivery: form.declaredPushDelivery });
  const showsLaunchPlan = sendsFromBackend({ sentFrom: form.sentFrom });
  const autoLiveAllowed = !(form.shadowPeriodDays > 0) && !(form.holdoutPercent > 0);
  const editableSurfaces = surfaces.filter(
    surface =>
      !isKlaviyoOwnedCopySurface({
        surface,
        sentFrom: form.sentFrom,
        pushDelivery: form.declaredPushDelivery,
        emailDelivery: form.declaredEmailDelivery
      })
  );

  const proposalGaps = form.declaredUserNotificationSetting === NEW_SWITCH ? switchProposalGaps(form.newSwitchDefinition) : [];
  const canCreate = form.name.trim() && /^[a-z0-9]+(_[a-z0-9]+)*$/.test(form.type) && form.audiences.length && !proposalGaps.length && !isSaving;
  const inputs = parseInputsText(form.inputsText);

  const emptySurfaces = editableSurfaces.filter(surface => DRAFTABLE_SURFACES.includes(surface) && !hasCopyWords(form.copy[surface]));
  // Surfaces and inputs Draft with AI filled and nobody has touched since: credited to it on Create, rewritten by Draft again.
  const isAiCopy = (current, surface) => surface in current.aiCopy && _.isEqual(current.copy[surface] || {}, current.aiCopy[surface]);
  const isAiInput = (current, input) => input.name in current.aiInputs && current.aiInputs[input.name] === input.example;
  const aiSurfaces = editableSurfaces.filter(surface => isAiCopy(form, surface));
  const aiInputNames = parseInputsText(form.inputsText)
    .filter(input => isAiInput(form, input))
    .map(input => input.name);
  const canDraft = canDraftFromPlan(form);
  const verb = form.copy.center_cta?.verb || '';
  const setVerb = next => set('copy', { ...form.copy, center_cta: next ? { verb: next } : {} });
  const planForPrompt = () => ({
    ..._.pick(form, [
      'name',
      'type',
      'sentFrom',
      'declaredPushDelivery',
      'declaredEmailDelivery',
      'audiences',
      'purpose',
      'triggerDescription',
      'notes',
      'declaredChannels',
      'declaredCenterBehavior',
      'declaredCenterWindow',
      'declaredCenterImageOf',
      'declaredThreadEntity'
    ]),
    declaredThreadStatuses: threadStatuses,
    centerBehavior: isCenter ? form.declaredCenterBehavior : 'none'
  });

  // Both buttons draft from Purpose and Trigger. Inputs adds names it does not have yet; Copy fills
  // the empty surfaces and leaves anything typed alone. Draft again replaces only what the AI wrote
  // the last time and nobody has touched since; the form itself remembers what that was, so an edit
  // made while a draft is on its way is kept.
  const draftInputs = () => {
    setDrafting('inputs');
    draftForNewNotification({ plan: planForPrompt(), want: 'inputs' })
      .then(result => {
        setForm(current => {
          const typed = parseInputsText(current.inputsText).filter(input => !isAiInput(current, input));
          const known = new Set(typed.map(input => input.name));
          const fresh = (result.inputs || []).filter(input => !known.has(input.name));
          return {
            ...current,
            inputsText: formatInputsText([...typed, ...fresh]),
            aiInputs: _.fromPairs(fresh.map(input => [input.name, input.example]))
          };
        });
        if (result.issues?.length) window.ALERT?.warn?.(result.issues.join(' '));
      })
      .catch(err => window.ALERT?.error?.(err?.error || 'Could not draft the inputs.'))
      .finally(() => setDrafting(null));
  };

  const surfacesToDraft = _.uniq([...emptySurfaces, ...aiSurfaces]);
  const draftCopy = () => {
    setDrafting('copy');
    draftForNewNotification({ plan: planForPrompt(), inputs: parseInputsText(form.inputsText), surfaces: surfacesToDraft, want: 'copy' })
      .then(result => {
        setForm(current => {
          // Only what was asked for, and only where the words are still the AI's or nobody has typed since.
          const taken = _.pickBy(
            result.copy || {},
            (content, surface) => surfacesToDraft.includes(surface) && (isAiCopy(current, surface) || !hasCopyWords(current.copy[surface]))
          );
          return { ...current, copy: { ...current.copy, ...taken }, aiCopy: { ...current.aiCopy, ...taken } };
        });
        if (result.issues?.length) window.ALERT?.warn?.(result.issues.join(' '));
      })
      .catch(err => window.ALERT?.error?.(err?.error || 'Could not draft the copy.'))
      .finally(() => setDrafting(null));
  };

  const create = (confirm = false) => {
    setIsSaving(true);
    const isNoSwitch = form.declaredUserNotificationSetting === NO_SWITCH;
    const body = {
      ..._.omit(form, ['typeEdited', 'ownerEdited', 'inputsText', 'copy', 'aiCopy', 'aiInputs', 'shadowPeriodDays', 'holdoutPercent', 'autoLive']),
      ...(showsLaunchPlan
        ? { shadowPeriodDays: form.shadowPeriodDays, holdoutPercent: form.holdoutPercent, autoLive: form.autoLive }
        : {}),
      sentFrom: form.sentFrom || null,
      inputs: parseInputsText(form.inputsText),
      declaredUserNotificationSetting:
        form.declaredUserNotificationSetting === NEW_SWITCH
          ? form.newSwitchDefinition?.setting || null
          : form.declaredUserNotificationSetting || null,
      noSwitchReason: isNoSwitch ? form.noSwitchReason : null,
      newSwitchDefinition: form.declaredUserNotificationSetting === NEW_SWITCH ? _.omit(form.newSwitchDefinition, 'settingEdited') : null,
      declaredCenterBehavior: isCenter ? form.declaredCenterBehavior : 'none',
      declaredCenterImageOf: isCenter ? form.declaredCenterImageOf : null,
      declaredCenterWindow: isCenter && form.declaredCenterBehavior === 'aggregated' ? form.declaredCenterWindow : null,
      declaredThreadEntity: isThread ? form.declaredThreadEntity.trim() || null : null,
      declaredThreadStatuses: isThread ? parseStatuses(form.declaredThreadStatuses) : null,
      declaredCenterAnchor: (isCenter && form.declaredCenterBehavior === 'aggregated' && form.declaredCenterAnchor.trim()) || null,
      declaredRequiresAction: isCenter ? !!form.declaredRequiresAction : null,
      declaredCtaStyle: isCenter && form.declaredRequiresAction ? form.declaredCtaStyle || 'primary' : null,
      declaredShowChevron: isCenter && !form.declaredRequiresAction ? !!form.declaredShowChevron : null,
      tapDestination: form.tapDestination.trim() || null,
      emergencyOff: form.emergencyOff.trim() || null,
      klaviyoFlowUrl: usesFlow ? form.klaviyoFlowUrl.trim() || null : null,
      declaredKlaviyoEventName: isDeliveredByKlaviyo ? form.declaredKlaviyoEventName.trim() || null : null,
      notificationCenterRationale: form.notificationCenterRationale.trim() || null,
      copy: _.pickBy(
        _.mapValues(form.copy, content => {
          const filled = casesOf(content).filter(rule => String(rule.text || '').trim());
          return filled.length ? { ...content, cases: filled } : _.omit(content, 'cases');
        }),
        (content, surface) => editableSurfaces.includes(surface) && hasCopyWords(content)
      ),
      aiDraftedSurfaces: aiSurfaces,
      ...(confirm ? { confirm: true } : {})
    };
    createNotificationRegistryEntry(body)
      .then(page => onCreated(page.entry.type))
      .catch(err => {
        if (err?.extra?.code === 'CONFIRMATION_REQUIRED') {
          const dialog = modeChangeConfirm({
            name: form.name.trim(),
            mode: 'live',
            fromMode: null,
            status: 'planned',
            holdoutPercent: form.holdoutPercent || 0,
            allowlistCount: 0
          });
          confirmAlert({
            title: dialog.title,
            message: dialog.message,
            buttons: [
              { label: 'Cancel', className: 'cancel' },
              { label: dialog.confirm, onClick: () => create(true) }
            ]
          });
          return;
        }
        window.ALERT?.error?.(err?.error || 'Could not create the notification.');
      })
      .finally(() => setIsSaving(false));
  };

  if (!options) return <div className='notifications-registry-new loading'>Loading…</div>;

  const admins = options.admins || [];

  return (
    <div className='notifications-registry-new'>
      <div className='new-header' style={{ top: stickyTop }}>
        <div>
          <button type='button' className='back-link' onClick={onCancel}>
            ← Registry
          </button>
          <h2>New Planned Notification</h2>
        </div>
        <div className='new-actions'>
          <button type='button' className='quiet' onClick={onCancel}>
            Cancel
          </button>
          <button type='button' className='primary' disabled={!canCreate} onClick={() => create()}>
            Create Planned Notification
          </button>
        </div>
      </div>

      <div className='new-body'>
        <div className='new-form'>
          <Field label='Name' tip={TIPS.name} hint='e.g. Payout sent'>
            <input data-testid='registry-new-name' value={form.name} onChange={e => setName(e.target.value)} />
          </Field>
          <Field label='Short code' tip={TIPS.shortCode} hint='e.g. payout_sent'>
            <input
              className='mono'
              value={form.type}
              onChange={e =>
                setForm({
                  ...form,
                  type: shortCodeFrom(e.target.value) + (e.target.value.endsWith('_') ? '_' : ''),
                  typeEdited: true
                })
              }
            />
          </Field>

          <div className='sent-from-field'>
            <Field
              label='Sent from'
              tip={TIPS.sentFrom}
              hint={(_.find(SENT_FROM_CHOICES, { value: sentFromChoice }) || SENT_FROM_UNDECIDED).description}
              optional
            >
              <SentFromSelect value={sentFromChoice} onChange={choice => setForm({ ...form, ...sentFromFieldsFor(choice) })} />
            </Field>
            {usesFlow && (
              <div className='sent-from-details'>
                {isDeliveredByKlaviyo && (
                  <Field label='Klaviyo event' hint={DELIVERED_BY_KLAVIYO_PLANNED_EVENT_HINT} optional>
                    <input className='mono' value={form.declaredKlaviyoEventName} onChange={e => set('declaredKlaviyoEventName', e.target.value)} />
                  </Field>
                )}
                <Field label='Klaviyo flow link' tip={TIPS.klaviyoUrl} hint={KLAVIYO_FLOW_LINK_PLANNED_HINT} optional>
                  <input
                    value={form.klaviyoFlowUrl}
                    placeholder='https://www.klaviyo.com/flow/ABC123/edit'
                    onChange={e => set('klaviyoFlowUrl', e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
          <Field label='Audience' tip={TIPS.audience} hint='who receives it'>
            <Chips values={options.audiences} selected={form.audiences} labels={AUDIENCE_LABELS} onToggle={toggleAudience} />
          </Field>

          <Field label='Channels' tip={TIPS.channels} optional>
            <Chips
              values={['push', 'email', 'center']}
              selected={form.declaredChannels}
              labels={CHANNEL_LABELS}
              onToggle={value => toggle('declaredChannels', value)}
            />
          </Field>
          <Field label='Budget category' tip={TIPS.budgetCategory} hint={options.capHintsByCategory?.[form.declaredBudgetCategory]} optional>
            <select value={form.declaredBudgetCategory} onChange={e => set('declaredBudgetCategory', e.target.value)}>
              {options.budgetCategories.map(value => (
                <option key={value} value={value}>
                  {humanizeBudgetCategory(value)}
                </option>
              ))}
            </select>
          </Field>
          <Field label='Mute switch' tip={TIPS.muteSwitch} optional>
            <SwitchSelect
              groups={settingsGroupsFor(options, form.audiences)}
              value={form.declaredUserNotificationSetting}
              onChange={value => set('declaredUserNotificationSetting', value)}
            />
            {form.declaredUserNotificationSetting === NO_SWITCH && (
              <>
                <input className='follow-up' value={form.noSwitchReason} onChange={e => set('noSwitchReason', e.target.value)} />
                <div className='hint'>why creators cannot turn this off, e.g. a security notice about her account</div>
              </>
            )}
            {form.declaredUserNotificationSetting === NEW_SWITCH && <div className='hint'>Fill in the details in the New switch section below.</div>}
          </Field>

          <Field label='Owner' tip={TIPS.owner} hint="defaults to the audience's owner" optional>
            <select
              value={form.Owner_User_id || ''}
              onChange={e => setForm({ ...form, Owner_User_id: e.target.value ? Number(e.target.value) : null, ownerEdited: true })}
            >
              <option value=''>Nobody yet</option>
              {admins.map(admin => (
                <option key={admin.id} value={admin.id}>
                  {admin.name || admin.email}
                  {admin.name ? ` · ${admin.email}` : ''}
                </option>
              ))}
            </select>
          </Field>

          {isCenter && (
            <>
              <Field label='Notification Center behavior' tip={TIPS.centerBehavior} optional>
                <select
                  data-testid='registry-new-center-behavior'
                  value={form.declaredCenterBehavior}
                  onChange={e => setCenterShape({ declaredCenterBehavior: e.target.value })}
                >
                  {options.centerBehaviors
                    .filter(value => value !== 'none')
                    .map(value => (
                      <option key={value} value={value}>
                        {CENTER_BEHAVIOR_LABELS[value]}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label='Needs action' tip={TIPS.needsAction}>
                <select value={form.declaredRequiresAction ? 'yes' : 'no'} onChange={e => set('declaredRequiresAction', e.target.value === 'yes')}>
                  <option value='no'>No</option>
                  <option value='yes'>Yes</option>
                </select>
              </Field>
              {form.declaredRequiresAction ? (
                <Field label='Button style' tip={TIPS.ctaStyle}>
                  <select value={form.declaredCtaStyle || 'primary'} onChange={e => set('declaredCtaStyle', e.target.value)}>
                    <option value='primary'>Primary</option>
                    <option value='secondary'>Secondary</option>
                  </select>
                </Field>
              ) : (
                <Field label='Chevron' tip={TIPS.showChevron}>
                  <select value={form.declaredShowChevron ? 'yes' : 'no'} onChange={e => set('declaredShowChevron', e.target.value === 'yes')}>
                    <option value='no'>No</option>
                    <option value='yes'>Yes</option>
                  </select>
                </Field>
              )}
              <Field label='Notification Center image' tip={TIPS.centerImage} optional>
                <select value={form.declaredCenterImageOf} onChange={e => set('declaredCenterImageOf', e.target.value)}>
                  {options.centerImageKinds.map(value => (
                    <option key={value} value={value}>
                      {CENTER_IMAGE_LABELS[value]}
                    </option>
                  ))}
                </select>
              </Field>
              {form.declaredCenterBehavior === 'aggregated' && (
                <>
                  <Field label='Aggregation window' hint='for Aggregated rows' optional>
                    <select
                      data-testid='registry-new-center-window'
                      value={form.declaredCenterWindow}
                      onChange={e => setCenterShape({ declaredCenterWindow: e.target.value })}
                    >
                      {options.centerWindows.map(value => (
                        <option key={value} value={value}>
                          Per {value}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label='Row anchor' tip={TIPS.rowAnchor} hint='for Aggregated rows' optional>
                    <input value={form.declaredCenterAnchor} placeholder='brand.id' onChange={e => set('declaredCenterAnchor', e.target.value)} />
                  </Field>
                  {isThread ? (
                    <>
                      <Field label='Thread on' tip={TIPS.threadOn} hint='e.g. order.id' optional>
                        <input
                          data-testid='registry-new-thread-on'
                          value={form.declaredThreadEntity}
                          onChange={e => set('declaredThreadEntity', e.target.value)}
                        />
                      </Field>
                      <Field label='Statuses the row moves through' tip={TIPS.threadStatuses} hint='in order, separated by commas' optional>
                        <input
                          data-testid='registry-new-thread-statuses'
                          value={form.declaredThreadStatuses}
                          onChange={e => set('declaredThreadStatuses', e.target.value)}
                        />
                        <div className='hint'>
                          Row key {form.type || 'short_code'}:{form.declaredThreadEntity.trim() || 'entity.id'} · no day bucket
                        </div>
                      </Field>
                    </>
                  ) : (
                    <div />
                  )}
                </>
              )}
            </>
          )}

          <Field
            label='Product area'
            tip={TIPS.productArea}
            hint={form.audiences.length ? undefined : 'choose an audience to see its areas'}
            full
            optional
          >
            <AreaChips
              areas={areaOptionsFor(options, form.audiences)}
              selected={form.productAreas}
              onToggle={value => toggle('productAreas', value)}
              onAdd={value => setForm(current => ({ ...current, productAreas: _.uniq([...current.productAreas, value]) }))}
            />
          </Field>

          <Field
            label='Purpose'
            tip={TIPS.purpose}
            hint='one sentence, e.g. Tell the creator the money moved, with how much and where, before the processor\u2019s own email arrives'
            full
            optional
          >
            <textarea rows={3} value={form.purpose} onChange={e => set('purpose', e.target.value)} />
          </Field>
          <Field
            label='Trigger'
            tip={TIPS.trigger}
            hint='the event or job that fires it, with the file in brackets, e.g. One push per Payments insert in the payout run [payout_run.js]'
            full
            optional
          >
            <textarea rows={3} value={form.triggerDescription} onChange={e => set('triggerDescription', e.target.value)} />
          </Field>
          <Field
            label='Inputs'
            tip={TIPS.inputs}
            hint='one per line, or several on a line split by / , or ;  e.g. amount: 907 / month: December'
            full
            optional
            action={
              <span className='draft-state'>
                {aiInputNames.length > 0 && <span className='drafted-tag'>Drafted with AI</span>}
                <DraftButton
                  onClick={canDraft ? draftInputs : null}
                  isDrafting={drafting === 'inputs'}
                  isBusy={!!drafting}
                  label={aiInputNames.length ? 'Draft again' : 'Draft with AI'}
                  title={
                    aiInputNames.length
                      ? 'Proposes the inputs again, replacing the ones the AI wrote; anything you typed stays.'
                      : 'Proposes the inputs from Purpose and Trigger; engineering confirms them.'
                  }
                />
              </span>
            }
          >
            <textarea className='mono' rows={4} value={form.inputsText} onChange={e => set('inputsText', e.target.value)} />
          </Field>
          <Field label='Tap lands' tip={TIPS.tapLands} hint='e.g. Payments, or https://shopmy.us/payments' full optional>
            <input value={form.tapDestination} onChange={e => set('tapDestination', e.target.value)} />
          </Field>
          <Field label='Emergency off' tip={TIPS.emergencyOff} hint='the fastest way to stop future sends' full optional>
            <textarea rows={2} value={form.emergencyOff} onChange={e => set('emergencyOff', e.target.value)} />
          </Field>
          <Field
            label='Notification Center rationale'
            tip={TIPS.notificationCenterRationale}
            hint='why this should or should not leave a row'
            full
            optional
          >
            <textarea rows={2} value={form.notificationCenterRationale} onChange={e => set('notificationCenterRationale', e.target.value)} />
          </Field>
          <Field label='Notes' tip={TIPS.notes} hint='fallbacks, variants, flags, e.g. Stripe payouts read Stripe as the method' full optional>
            <textarea rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </Field>
          {showsLaunchPlan && canChangeRollout && (
            <div className='launch-choices'>
              <Field label='Shadow period' tip='How long it must sit in Shadow, with at least one send to read, before Allowlist and Live unlock. The default is none. Every change is in History.'>
                <YesNo
                  on={form.shadowPeriodDays > 0}
                  onChange={on => setForm(current => ({ ...current, shadowPeriodDays: on ? 7 : 0, ...(on ? { autoLive: false } : {}) }))}
                />
              </Field>
              <Field label='Holdout group' tip={TIPS.holdout}>
                <YesNo
                  on={form.holdoutPercent > 0}
                  onChange={on => setForm(current => ({ ...current, holdoutPercent: on ? 10 : 0, ...(on ? { autoLive: false } : {}) }))}
                />
              </Field>
              <Field label='Auto-Live' tip={AUTO_LIVE_NOTE}>
                <YesNo on={form.autoLive} disableYes={!autoLiveAllowed} onChange={on => set('autoLive', on)} />
                {!autoLiveAllowed && <div className='auto-live-warn'>{AUTO_LIVE_REQUIRES_NO}</div>}
              </Field>
            </div>
          )}
        </div>

        {form.declaredUserNotificationSetting === NEW_SWITCH && (
          <SwitchBuilder
            groups={settingsGroupsFor(options, form.audiences)}
            definition={form.newSwitchDefinition}
            onChange={definition => set('newSwitchDefinition', definition)}
          />
        )}

        <div className='copy-card'>
          <div className='card-head'>
            <span className='card-title'>
              Copy
              <InfoTip message='Optional. Anything typed here is saved as a draft when you create the notification; anything left empty shows as Needs copy on its page.' />
            </span>
            <span className='card-note'>optional · saved as drafts when you press Create</span>
            {aiSurfaces.length > 0 && <span className='drafted-tag'>Drafted with AI</span>}
            {surfacesToDraft.length > 0 && (
              <DraftButton
                onClick={canDraft ? draftCopy : null}
                isDrafting={drafting === 'copy'}
                isBusy={!!drafting}
                label={aiSurfaces.length ? 'Draft again' : 'Draft with AI'}
                title={
                  aiSurfaces.length ? 'Writes the AI\u2019s surfaces again and fills any still empty; anything you typed stays.' : TIPS.draftWithAi
                }
              />
            )}
          </div>
          {isDeliveredByKlaviyo && <div className='copy-surface muted flow-note'>{DELIVERED_BY_KLAVIYO_COPY_NOTE}</div>}
          {form.sentFrom === 'klaviyo_flow' && form.declaredChannels.includes('push') && (
            <div className='copy-surface muted'>{KLAVIYO_PUSH_COPY_NOTE}</div>
          )}
          {form.sentFrom === 'klaviyo_flow' && form.declaredChannels.includes('email') && (
            <div className='copy-surface muted'>{KLAVIYO_EMAIL_COPY_NOTE}</div>
          )}
          {editableSurfaces.map(surface => (
            <div key={surface} className='copy-surface'>
              <div className='copy-surface-head'>
                <span className='surface-name'>{SURFACE_LABELS[surface]}</span>
                {SURFACE_NOTES[surface] && <span className='surface-note'>{SURFACE_NOTES[surface]}</span>}
              </div>
              {surface === 'center_cta' && (
                <label className={cn('copy-field-row', { over: verb.length > SURFACE_FIELDS.center_cta.verb })}>
                  <span className='field-name'>Button verb</span>
                  <ButtonVerbField
                    className='copy-field'
                    verb={verb}
                    isCustom={isCustomVerb}
                    onPick={picked => {
                      setIsCustomVerb(false);
                      setVerb(picked);
                    }}
                    onCustom={() => setIsCustomVerb(true)}
                    onType={setVerb}
                  />
                </label>
              )}
              {surface !== 'center_cta' &&
                Object.keys(SURFACE_FIELDS[surface])
                  .filter(field => !(isThread && surface === 'center_row' && field === 'text'))
                  .map(field => {
                    const value = form.copy[surface]?.[field] || '';
                    const limit = SURFACE_FIELDS[surface][field];
                    const renderedLength = interpolateWithExamples(value, inputs).length;
                    const overLimit = limit && renderedLength > limit;
                    const onChange = e => set('copy', { ...form.copy, [surface]: { ...(form.copy[surface] || {}), [field]: e.target.value } });
                    return (
                      <label key={field} className={cn('copy-field-row', { over: overLimit })}>
                        <span className='field-name'>{copyFieldLabel(surface, field)}</span>
                        {isParagraphField(surface, field) ? (
                          <textarea className='copy-field' rows={5} value={value} onChange={onChange} />
                        ) : (
                          <input className='copy-field' value={value} onChange={onChange} />
                        )}
                        <span className='field-guide'>
                          {fieldGuideFor(surface, field)}
                          {limit && (
                            <span className={cn('counter', { over: overLimit })}>
                              {renderedLength} / {limit} recommended
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
              {surface === 'email_subject' && isDeliveredByKlaviyo && <div className='surface-flow-body'>{DELIVERED_BY_KLAVIYO_EMAIL_BODY}</div>}
              {surface === 'center_row_several' && (
                <CopyCases
                  cases={casesOf(form.copy[surface])}
                  inputs={inputs}
                  onChange={cases =>
                    set('copy', {
                      ...form.copy,
                      [surface]: cases.length ? { ...(form.copy[surface] || {}), cases } : _.omit(form.copy[surface] || {}, 'cases')
                    })
                  }
                />
              )}
              {surface === 'center_row' && threadStatuses.length > 0 && (
                <ThreadCopyCases
                  cases={statusCases(form.copy[surface], threadStatuses)}
                  statuses={threadStatuses}
                  inputs={inputs}
                  onChange={cases =>
                    set('copy', {
                      ...form.copy,
                      [surface]: { ...(form.copy[surface] || {}), cases }
                    })
                  }
                />
              )}
            </div>
          ))}
          {!surfaces.length && <div className='copy-surface muted'>Choose a channel to see its surfaces.</div>}
        </div>
      </div>
    </div>
  );
};

NotificationsRegistryNew.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  canChangeRollout: PropTypes.bool,
  stickyTop: PropTypes.number
};

const YesNo = ({ on, onChange, disableYes = false }) => (
  <div className='chips'>
    <button type='button' className={cn('chip', { on })} disabled={disableYes} onClick={() => onChange(true)}>
      Yes
    </button>
    <button type='button' className={cn('chip', { on: !on })} onClick={() => onChange(false)}>
      No
    </button>
  </div>
);

YesNo.propTypes = {
  on: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disableYes: PropTypes.bool
};

// The areas for the audiences chosen, in each audience's order and named once, then the areas people
// typed as Other on past notifications, so one spelling wins.
const areaOptionsFor = (options, audiences) =>
  _.uniq([..._.flatMap(audiences, audience => options.productAreasByAudience?.[audience] || []), ...(options.otherProductAreas || [])]);

// Product area chips with an Other chip that takes a typed area. A chosen area that no longer belongs
// to the audiences picked stays visible until it is turned off.
const AreaChips = ({ areas, selected, onToggle, onAdd }) => {
  const [isTyping, setIsTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const shown = _.uniq([...areas, ...selected]);
  const add = () => {
    const value = typed.trim();
    if (value && !selected.includes(value)) onAdd(value);
    setTyped('');
    setIsTyping(false);
  };
  return (
    <div className='chips'>
      {shown.map(value => (
        <button key={value} type='button' className={cn('chip', { on: selected.includes(value) })} onClick={() => onToggle(value)}>
          {value}
        </button>
      ))}
      {isTyping ? (
        <input
          autoFocus
          className='chip-input'
          value={typed}
          onChange={e => setTyped(e.target.value)}
          onBlur={add}
          onKeyDown={e => {
            if (e.key === 'Enter') add();
            if (e.key === 'Escape') {
              setTyped('');
              setIsTyping(false);
            }
          }}
        />
      ) : (
        <button type='button' className='chip other' onClick={() => setIsTyping(true)}>
          Other…
        </button>
      )}
    </div>
  );
};

AreaChips.propTypes = {
  areas: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired
};

// The switches as the Settings page groups them, No switch first, Create at the foot.
const SwitchSelect = ({ groups, value, onChange }) => (
  <select value={value} onChange={e => onChange(e.target.value)}>
    <option value=''>Choose the setting creators use to turn this off</option>
    <option value={NO_SWITCH}>No switch · cannot be muted</option>
    <option value={NEW_SWITCH}>+ Create a new switch…</option>
    {groups.map(group => (
      <optgroup key={group.title} label={group.title}>
        {group.options.map(option => (
          <option key={option.setting} value={option.setting}>
            {option.label} · {option.setting}
            {option.notificationCount ? ` · ${option.notificationCount}` : ''}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

SwitchSelect.propTypes = { groups: PropTypes.array.isRequired, value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

// Different words for a day that matches a condition; the sentence above covers all other days.
const CopyCases = ({ cases, inputs, onChange }) => {
  const limit = SURFACE_FIELDS.center_row_several.text;
  const update = (index, changes) => onChange(cases.map((rule, at) => (at === index ? { ...rule, ...changes } : rule)));
  return (
    <div className='copy-cases'>
      {cases.map((rule, index) => {
        const renderedLength = interpolateWithExamples(rule.text || '', inputs).length;
        const overLimit = limit && renderedLength > limit;
        return (
          <div key={index} className='copy-case'>
            <div className='case-condition'>
              <span className='case-number'>Case {index + 1}</span>
              <span className='case-when'>when</span>
              <select value={rule.path || ''} onChange={e => update(index, { path: e.target.value })}>
                {CASE_FIELDS.map(field => (
                  <option key={field} value={field}>
                    {caseFieldLabel(field)}
                  </option>
                ))}
              </select>
              <select value={rule.op || ''} onChange={e => update(index, { op: e.target.value })}>
                {CASE_OPERATORS.map(op => (
                  <option key={op} value={op}>
                    {CASE_OPERATOR_LABELS[op]}
                  </option>
                ))}
              </select>
              <input
                className='case-value'
                type='number'
                min='0'
                value={rule.value ?? ''}
                onChange={e => update(index, { value: e.target.value === '' ? '' : Number(e.target.value) })}
              />
              <button type='button' className='quiet' onClick={() => onChange(cases.filter((other, at) => at !== index))}>
                Remove
              </button>
            </div>
            <label className={cn('copy-field-row', { over: overLimit })}>
              <input
                className='copy-field'
                placeholder='The sentence for a day this case matches'
                value={rule.text || ''}
                onChange={e => update(index, { text: e.target.value })}
              />
              <span className='field-guide'>
                Same variables as the sentence above.
                {limit && (
                  <span className={cn('counter', { over: overLimit })}>
                    {renderedLength} / {limit} recommended
                  </span>
                )}
              </span>
            </label>
          </div>
        );
      })}
      <button
        type='button'
        className='quiet add-case'
        onClick={() => onChange([...cases, { path: CASE_FIELDS[0], op: CASE_OPERATORS[0], value: '', text: '' }])}
      >
        Add a case
        <InfoTip message='A different sentence for a day this condition matches, like three or more arrivals. The sentence above covers every other day.' />
      </button>
    </div>
  );
};

CopyCases.propTypes = { cases: PropTypes.array.isRequired, inputs: PropTypes.array.isRequired, onChange: PropTypes.func.isRequired };

// One sentence per status the row moves through. The conditions are the statuses themselves; only
// the words are written here. Empty sentences stay off the draft until they have words.
const ThreadCopyCases = ({ cases, statuses, inputs, onChange }) => {
  const limit = SURFACE_FIELDS.center_row.text;
  const update = (index, text) => onChange(cases.map((rule, at) => (at === index ? { ...rule, text } : rule)));
  return (
    <div className='copy-cases'>
      {cases.map((rule, index) => {
        const renderedLength = interpolateWithExamples(rule.text || '', inputs).length;
        const overLimit = limit && renderedLength > limit;
        const stillSent = statuses.includes(String(rule.value));
        return (
          <div key={String(rule.value)} className='copy-case'>
            <div className='case-condition'>
              <span className='case-number'>{index + 1}</span>
              <span className='case-when'>when it becomes</span>
              <span className='case-locked'>
                {statusLabelOf(rule.value)}
                {stillSent ? '' : ' · no longer sent'}
              </span>
            </div>
            <label className={cn('copy-field-row', { over: overLimit })}>
              <input
                className='copy-field'
                placeholder={`The sentence when it becomes ${statusLabelOf(rule.value)}`}
                value={rule.text || ''}
                onChange={e => update(index, e.target.value)}
              />
              <span className='field-guide'>
                Same variables as the sentence above.
                {limit && (
                  <span className={cn('counter', { over: overLimit })}>
                    {renderedLength} / {limit} recommended
                  </span>
                )}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
};

ThreadCopyCases.propTypes = {
  cases: PropTypes.array.isRequired,
  statuses: PropTypes.array.isRequired,
  inputs: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
};

export default NotificationsRegistryNew;
